/**
 * 版本資訊（VersionBadge 用）：`web` / `cocos` / `server` 三個來源各自獨立取得，任一失敗不影響其他、
 * 也不擋任何流程。GAME_READY 前顯示在 Cocos iframe 左下角（App.vue），之後隨 badge 一起消失。
 *
 *   - `web`   ：build-time `__APP_VERSION__`（vite define ← package.json version）。
 *   - `cocos` ：`fetch(<gameUrl 同目錄>/gameConfig.json, { cache: 'no-store' })` → `gameVersion`。
 *               gameUrl 一有值就發、跟 iframe 載入並行；線上與 host 同網域無 CORS；本地 dev 指到
 *               dev-h5 domain 需對方開 CORS，拿不到就維持 '-'。
 *   - `server`：`GET /v1/{gameCode}/version`（`fetchGameVersion`）。**端點要 token**
 *               （2026-09-07 後端改版；race 探針另走免 token 的 /healthz）→ 等 `authReady`
 *               才發。badge 只在 GAME_READY 前可見，登入慢時 server 值可能來不及顯示，
 *               屬已知取捨（端點要求如此，沒得提早）。
 *
 * 取不到一律顯示 '-'（不顯示 error 字樣）。三個值任一更新就 `console.info('[web][Version] …')` 一行。
 */
import { ref, watch, type Ref } from 'vue'
import { fetchGameVersion } from '@/api/game'

export const VERSION_UNKNOWN = '-'

interface CocosGameConfig {
  gameId?: string
  gameName?: string
  gameVersion?: string
  isDev?: boolean
}

/** gameUrl（…/games/<code>/index.html）→ 同目錄 gameConfig.json */
export const cocosGameConfigUrl = (gameUrl: string): string => {
  const i = gameUrl.lastIndexOf('/')
  return `${i >= 0 ? gameUrl.slice(0, i) : gameUrl}/gameConfig.json`
}

export async function fetchCocosVersion(gameUrl: string, fetchFn: typeof fetch = fetch): Promise<string> {
  try {
    const res = await fetchFn(cocosGameConfigUrl(gameUrl), { cache: 'no-store' })
    if (!res.ok) return VERSION_UNKNOWN
    const json = (await res.json()) as CocosGameConfig
    const v = typeof json?.gameVersion === 'string' ? json.gameVersion.trim() : ''
    return v || VERSION_UNKNOWN
  } catch {
    return VERSION_UNKNOWN
  }
}

/**
 * 後端 runner 版本（`GET /v1/{gameCode}/version`）。
 * 空 gameCode / 取不到 / 後端沒開這款 → `VERSION_UNKNOWN`（不 throw，見 `fetchGameVersion`）。
 */
export async function fetchServerVersion(gameCode: string | null | undefined): Promise<string> {
  const code = String(gameCode ?? '').trim()
  if (!code) return VERSION_UNKNOWN
  return (await fetchGameVersion({ gameCode: code })) || VERSION_UNKNOWN
}

export function useVersionInfo(
  gameUrl: Ref<string | null | undefined>,
  gameId: Ref<string | null | undefined>,
  authReady: Ref<boolean>,
) {
  const web = ref<string>(typeof __APP_VERSION__ === 'string' && __APP_VERSION__ ? __APP_VERSION__ : VERSION_UNKNOWN)
  const cocos = ref<string>(VERSION_UNKNOWN)
  const server = ref<string>(VERSION_UNKNOWN)

  let seq = 0
  watch(
    gameUrl,
    (url) => {
      const my = ++seq
      cocos.value = VERSION_UNKNOWN
      if (!url) return
      void fetchCocosVersion(url).then((v) => {
        if (my === seq) cocos.value = v
      })
    },
    { immediate: true },
  )

  // server 版本：端點**要 token**（2026-09-07 改版）→ gameId 有值且 `authReady` 才打。
  //   seq 防競態：快速換款 / auth 重來時晚回的舊請求不覆蓋新值。
  let serverSeq = 0
  watch(
    [gameId, authReady],
    ([id, ready]) => {
      const my = ++serverSeq
      server.value = VERSION_UNKNOWN
      if (!id || !ready) return
      void fetchServerVersion(id).then((v) => {
        if (my === serverSeq) server.value = v
      })
    },
    { immediate: true },
  )

  // console 也印一份（值一到就更新印一行；release build 的 console 會被 esbuild drop，屬既有政策）
  watch(
    [server, cocos, web],
    ([s, c, w]) => {
      console.info(`[web][Version] server: ${s} | cocos: ${c} | web: ${w}`)
    },
    { immediate: true },
  )

  return { web, cocos, server }
}
